import {useCallback, useMemo, useState} from 'react';
import {Modal} from '@/ui/modal/modal';
import {v4 as uuidv4} from 'uuid';
import {usePhoneAuth} from './use-phone-auth';
import {CodeInputScreen} from './screens/code-input-screen';
import {PhoneInputScreen} from './screens/phone-input-screen';
import {FirstScreen} from './screens/first-screen';

import styles from './login-modal.module.css';
import {useInfo} from '@/app/infoContext';
import {Drawer} from 'antd';
import {usePollingQuery} from '@/api/telegramAuth';
import {MOCKED_TOKEN, TELEGRAM_AUTH_BOT_URL} from '@/consts';
import {useRouter} from 'next/navigation';

export type ScreenId = 'first' | 'phoneInput' | 'codeInput';
export type ModalType = 'login' | 'update';

interface LoginModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onLogin: () => void;
    initScreen?: ScreenId;
    type?: ModalType;
}

export const LoginModal = ({isOpen, setIsOpen, onLogin, initScreen = 'first', type = 'login'}: LoginModalProps) => {
    const {isMobile} = useInfo();
    const router = useRouter();
    const {
        screenId,
        timeLeft,
        phoneNumber,
        setScreenId,
        setPhoneNumber,
        setCode,
        handlePhoneSubmit,
        handleCodeSubmit,
        codeError,
    } = usePhoneAuth({setIsOpen, onLogin, initScreen, type});

    const handleClose = useCallback(() => {
        setScreenId(initScreen);
        setPhoneNumber('');
        setCode('');
        setIsOpen(false);
    }, [setIsOpen, setScreenId, setPhoneNumber, setCode, initScreen]);

    const [pollingTimeout, setPollingTimeout] = useState<NodeJS.Timeout>();
    const [startPolling, setStartPolling] = useState(false);

    usePollingQuery(uuid, startPolling, () => {
        setStartPolling(false);
        setScreenId('first');
        setIsOpen(false);
        router.push('/user');
    });

    const handleTelegramLogin = useCallback(() => {
        window.open(TELEGRAM_BOT_URL(), '_blank')?.focus();

        // Очищаем предыдущий таймаут
        if (pollingTimeout) clearTimeout(pollingTimeout);

        // Запускаем polling через 15 секунд
        const timeout = setTimeout(() => {
            setStartPolling(true);

            // Останавливаем через 5 минут
            setTimeout(() => setStartPolling(false), 5 * 60_000);
        }, 2_000);

        setPollingTimeout(timeout);

        return () => {
            if (pollingTimeout) clearTimeout(pollingTimeout);
        };
    }, [setStartPolling, setPollingTimeout, pollingTimeout]);

    const screens = useMemo(
        () => ({
            first: (
                <FirstScreen
                    onTelegramLogin={handleTelegramLogin}
                    onPhoneLogin={() => {
                        setScreenId('phoneInput');
                    }}
                    onGuestAuth={() => {
                        setIsGuest();
                        handleClose();
                    }}
                    isTelegramEnabled={false}
                    isPhoneEnabled={true}
                    isGuestEnabled={!!MOCKED_TOKEN}
                />
            ),
            phoneInput: (
                <PhoneInputScreen
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                    onPhoneSubmit={handlePhoneSubmit}
                    type={type}
                />
            ),
            codeInput: (
                <CodeInputScreen
                    phoneNumber={'+7' + phoneNumber}
                    timeLeft={timeLeft}
                    onCodeChange={setCode}
                    onCodeSubmit={handleCodeSubmit}
                    onResendCode={() => {
                        setCode('');
                        handlePhoneSubmit();
                    }}
                    error={codeError}
                />
            ),
        }),
        [
            startPolling,
            codeError,
            type,
            phoneNumber,
            timeLeft,
            handlePhoneSubmit,
            handleCodeSubmit,
            handleTelegramLogin,
            setScreenId,
            setPhoneNumber,
            setCode,
            startPolling,
            handleClose,
        ],
    );

    return isMobile ? (
        <Drawer open={isOpen} onClose={handleClose} size="large">
            <div className={styles.loginModalContent}>{screens[screenId]}</div>
        </Drawer>
    ) : (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <div className={styles.loginModalContent}>{screens[screenId]}</div>
        </Modal>
    );
};

export function validateCode(code: string): boolean {
    return /^\d{6}$/.test(code);
}

const uuid = uuidv4();

const TELEGRAM_BOT_URL = () => `${TELEGRAM_AUTH_BOT_URL}?start=${uuid}`;

export const getIsAuthorized = () => {
    if (typeof window === 'undefined') return null;
    const isGuest = localStorage.getItem('guest');
    const token = localStorage.getItem('authToken');
    if (isGuest === '1' && MOCKED_TOKEN) {
        return MOCKED_TOKEN;
    } else {
        return token;
    }
};

export const unAuth = () => {
    if (typeof window !== 'undefined') {
        localStorage.removeItem('guest');
        localStorage.removeItem('authToken');
    }
};

export const auth = (token: string) => {
    if (typeof window !== 'undefined') {
        unAuth();
        localStorage.setItem('authToken', token);
    }
};

export const getIsGuest = () => {
    if (typeof window !== 'undefined') {
        return localStorage.getItem('guest') === '1';
    }
};

export const setIsGuest = () => {
    if (typeof window !== 'undefined') {
        localStorage.setItem('guest', '1');
    }
};
